import axios from "axios";
import { installResaAudit } from "./resaHistory";
import { noteLegacyFailure, noteLegacySuccess } from "./legacyApiStatus";

// API externe Casanéo (backend existant en production) - ce n'est PAS notre backend.
export const API_BASE = process.env.REACT_APP_LEGACY_API_BASE || "https://rental-hub-manager.emergent.host/api";
export const TOKEN_KEY = "casaneo_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API_BASE, timeout: 45000 });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// --- Bascule lecture locale (Phase 2 de la sortie de Lodgify) ---------------
// Quand la source est « local », les LECTURES de /reservations et /properties
// partent vers le backend Casanéo. Les ÉCRITURES CRUD principales (POST/PUT/
// DELETE) partent aussi en local (Phase C) — le backend écrit dans Mongo
// puis rejoue vers legacy via `legacy_replay_queue`. Les endpoints spécialisés
// (paiements, commission, checkout, envoi de clés…) continuent d'aller au
// legacy en direct — ils seront proxy-fiés une fois le CRUD MVP éprouvé.
export const DATA_SOURCE_KEY = "casaneo_data_source";
export const getDataSource = () => {
  try { return localStorage.getItem(DATA_SOURCE_KEY) || "legacy"; } catch { return "legacy"; }
};
export const setDataSource = (s) => {
  try { localStorage.setItem(DATA_SOURCE_KEY, s); } catch { /* noop */ }
};

const LOCAL_READS = ["/reservations", "/properties", "/owners", "/dashboard", "/analytics/revenue"];
// Extra GET paths (nested/computed) that must also be routed to the
// local Casanéo backend when the tenant opted in.
const LOCAL_READ_MATCHERS = [
  /^\/properties\/[^/]+$/,         // GET /properties/{id} (fiche logement, périmètre propriétaire)
  /^\/owners\/[^/]+$/,             // GET /owners/{id}
  /^\/owners\/[^/]+\/summary$/,    // GET /owners/{id}/summary
];
// Only the 6 core CRUD paths get proxied — actions like /payments,
// /commission, /checkout stay on legacy for now.
const LOCAL_WRITE_MATCHERS = [
  /^\/reservations$/,          // POST create
  /^\/reservations\/[^/]+$/,   // PUT & DELETE by id (excludes /reservations/{id}/anything)
  /^\/properties$/,            // POST create
  /^\/properties\/[^/]+$/,     // PUT & DELETE by id
  /^\/owners$/,                // POST create
  /^\/owners\/[^/]+$/,         // PUT & DELETE by id
  /^\/reservations\/[^/]+\/paid$/, // PATCH « marquer encaissé »
  /^\/reservations\/[^/]+\/status$/, // PATCH statut (annulation manuelle…)
  /^\/reservations\/[^/]+\/payments$/,        // POST acompte / encaissement partiel
  /^\/reservations\/[^/]+\/payments\/[^/]+$/, // DELETE d'un encaissement
  /^\/reservations\/[^/]+\/send-keys$/,        // POST renvoi instructions clés (modèle Casanéo : livret, adresse, remise des clés)
  /^\/reservations\/[^/]+\/caution-validated$/, // PATCH validation caution (+ envoi clés)
];

api.interceptors.request.use((config) => {
  if (getDataSource() !== "local") return config;
  const method = (config.method || "get").toLowerCase();
  const path = (config.url || "").split("?")[0].replace(/\/$/, "");

  // GET: /reservations, /properties, /owners → /api/local/*
  if (method === "get" && LOCAL_READS.includes(path)) {
    return _redirectToLocal(config);
  }
  // GET nested: /owners/{id}, /owners/{id}/summary → /api/local/*
  if (method === "get" && LOCAL_READ_MATCHERS.some((re) => re.test(path))) {
    return _redirectToLocal(config);
  }
  // Writes: only the 6 core CRUD paths.
  if (["post", "put", "patch", "delete"].includes(method) && LOCAL_WRITE_MATCHERS.some((re) => re.test(path))) {
    return _redirectToLocal(config);
  }
  return config;
});

function _redirectToLocal(config) {
  config.baseURL = `${process.env.REACT_APP_BACKEND_URL}/api/local`;
  // Voir localApi.js pour le pourquoi — l'ingress renvoie `*` avec
  // `credentials: true`, combinaison bloquée par tous les navigateurs
  // dès que le frontend n'a pas la même origine que l'API. Bearer only.
  config.withCredentials = false;
  let cn = null;
  try { cn = localStorage.getItem("cn_bearer_token"); } catch { /* noop */ }
  config.headers = config.headers || {};
  if (cn) config.headers.Authorization = `Bearer ${cn}`;
  return config;
}

// Local audit trail for every reservation write.
installResaAudit(api);

// A single transient 401 from the legacy API used to wipe the session token,
// after which every subsequent call fired without Authorization and the pages
// stayed stuck on "Chargement…". Only drop the session after two consecutive
// 401s (any success resets the counter).
let consecutive401 = 0;

api.interceptors.response.use(
  (res) => {
    consecutive401 = 0;
    noteLegacySuccess();
    return res;
  },
  (err) => {
    noteLegacyFailure(err);
    if (err?.response?.status === 401) {
      consecutive401 += 1;
      const hadToken = !!getToken();
      if (consecutive401 >= 2) {
        clearToken();
        // Only hard-redirect when the user actually had a legacy session
        // that just expired. If we never had one (Casanéo-cookie-only
        // account), we'd otherwise bounce back to /login on every legacy
        // GET fired by the home dashboard → infinite login-loop.
        if (hadToken && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    } else if (err?.response) {
      consecutive401 = 0;
    }
    return Promise.reject(err);
  }
);

// Certaines réponses listes sont enveloppées ({items:[]}, {reservations:[]}, ...)
export const asList = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  for (const k of ["items", "reservations", "properties", "owners", "transactions", "threads", "messages", "rate_plans", "rooms", "blocks", "data", "results"]) {
    if (Array.isArray(data[k])) return data[k];
  }
  return [];
};
